import { useEffect, useRef, useState } from "react";
import {
  ArScanNotFoundError,
  fetchScanTarget,
  type ArScanTarget,
} from "../lib/arApi";
import {
  OVERLAY_VIDEO_STYLE,
  buildSceneSpec,
  inAppBrowserName,
  isInAppBrowser,
  loadArEngine,
  parseTrackingTuning,
  probeCamera,
} from "../lib/arScene";

type Phase =
  | "loading"
  | "in-app"
  | "not-found"
  | "failed"
  | "ready"       // 카메라·엔진 준비됨, 비추면 된다
  | "video-only"; // 카메라 없이 영상만 보기

/**
 * 키링을 비추는 화면(`/k/:shortId`). **로그인 없음.**
 *
 * 🚨 이 화면의 가장 큰 위험은 «검은 화면» 이다. v1 의 7/29 QA 미해결 4건 중 3건이 그것이었고,
 *    아이가 종이를 들고 서 있는데 화면이 아무 말도 안 하는 상황이 된다. 그래서 실패할 수 있는
 *    모든 자리(앱 안 브라우저·카메라 거부·엔진 로드 실패)마다 **이유와 다음 행동**을 준다.
 *
 * 🔑 마지막 안전망은 «카메라 없이 영상만 보기» 다. AR 이 안 되는 기기에서도 아이는
 *    자기 영상을 볼 수 있어야 한다 — 키링이 가리키는 것은 결국 그 영상이다.
 */
export function ArScanPage({ shortId }: { shortId: string }) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [target, setTarget] = useState<ArScanTarget | null>(null);
  const [reason, setReason] = useState<string>("");
  const [muted, setMuted] = useState(true);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const scanned = await fetchScanTarget(shortId);
        if (cancelled) return;
        setTarget(scanned);
        if (isInAppBrowser(navigator.userAgent)) {
          setPhase("in-app");
          return;
        }
        const camera = await probeCamera(navigator.mediaDevices);
        if (cancelled) return;
        if (camera !== "ok") {
          setReason(camera === "denied"
            ? "카메라를 쓸 수 없어서 영상만 보여 드려요."
            : "이 기기에서는 카메라를 열 수 없어서 영상만 보여 드려요.");
          setPhase("video-only");
          return;
        }
        await loadArEngine();
        if (cancelled) return;
        setPhase("ready");
      } catch (error) {
        if (cancelled) return;
        if (error instanceof ArScanNotFoundError) {
          setPhase("not-found");
          return;
        }
        setReason("연결이 불안정해요. 잠시 뒤 다시 열어 주세요.");
        setPhase("failed");
      }
    })();
    return () => { cancelled = true; };
  }, [shortId]);

  useEffect(() => {
    if (phase !== "ready" || !target || !hostRef.current) return;
    const tuning = parseTrackingTuning(window.location.search);
    const spec = buildSceneSpec(
      { markerUrl: target.marker_url, videoUrl: target.video_url },
      tuning,
    );

    // 🔑 v1(우진학교) 방식. MindAR 장면은 «그림을 보고 있는가» 의 감지기로만 쓴다 —
    //    3D 로 아무것도 그리지 않는다. 영상은 화면 중앙 «고정» HTML <video> 로 띄운다.
    //    3D 판은 40mm 마커의 프레임별 자세 잡음을 그대로 물려받아 세 번의 시도(마커
    //    재컴파일·필터 튜닝·자세 안정화)로도 떨림이 남았다. 고정이면 구조적으로 0 이다.
    const scene = document.createElement("a-scene");
    for (const [name, value] of Object.entries(spec.scene)) scene.setAttribute(name, value);

    const entity = document.createElement("a-entity");
    entity.setAttribute("mindar-image-target", "targetIndex: 0");
    scene.appendChild(entity);
    scene.appendChild(document.createElement("a-camera"));

    const video = document.createElement("video");
    video.id = "ar-keyring-video";
    // 🚨 카메라 <video> 를 화면 전체로 덮는 규칙(styles.css)이 이 오버레이까지 잡지 않도록
    //    표를 붙인다. 빼면 학생 영상이 화면을 꽉 채우며 늘어난다.
    video.className = "ar-overlay-video";
    for (const [name, value] of Object.entries(spec.video)) video.setAttribute(name, value);
    video.muted = spec.videoProperties.muted;
    video.loop = spec.videoProperties.loop;
    video.playsInline = spec.videoProperties.playsInline;
    for (const [name, value] of Object.entries(OVERLAY_VIDEO_STYLE)) {
      video.style.setProperty(name.replace(/[A-Z]/gu, (ch) => `-${ch.toLowerCase()}`), value);
    }
    // 그림이 잡히기 전에는 숨긴다 — 빈 검은 판이 화면 한가운데 떠 있으면 안 된다.
    video.style.display = "none";
    videoRef.current = video;

    const onFound = (): void => {
      video.style.display = "block";
      void video.play().catch(() => undefined);
    };
    const onLost = (): void => {
      video.style.display = "none";
      video.pause();
      video.currentTime = 0;
    };
    entity.addEventListener("targetFound", onFound);
    entity.addEventListener("targetLost", onLost);

    hostRef.current.appendChild(scene);
    hostRef.current.appendChild(video);
    return () => {
      entity.removeEventListener("targetFound", onFound);
      entity.removeEventListener("targetLost", onLost);
      video.pause();
      videoRef.current = null;
      video.remove();
      scene.remove();
    };
  }, [phase, target]);

  const toggleSound = (): void => {
    const video = videoRef.current;
    const next = !muted;
    setMuted(next);
    if (video) {
      video.muted = next;
      if (!next) void video.play().catch(() => undefined);
    }
  };

  if (phase === "loading") {
    return <ArMessage title="키링을 여는 중이에요" body="잠시만 기다려 주세요." />;
  }
  if (phase === "not-found") {
    return (
      <ArMessage
        title="키링을 찾을 수 없어요"
        body="QR 이 잘 안 읽혔을 수 있어요. 다시 한 번 비춰 보거나 선생님께 물어봐 주세요."
      />
    );
  }
  if (phase === "failed") {
    return <ArMessage title="지금은 열 수 없어요" body={reason} />;
  }
  if (phase === "in-app") {
    const name = inAppBrowserName(navigator.userAgent) ?? "이 앱";
    return (
      <ArMessage
        title="다른 브라우저로 열어 주세요"
        body={`${name} 안에서는 카메라를 쓸 수 없어요. 오른쪽 위 메뉴에서 «다른 브라우저로 열기» 를 눌러 주세요.`}
      >
        <button
          type="button"
          className="button-secondary"
          onClick={() => void navigator.clipboard?.writeText(window.location.href)}
        >
          주소 복사하기
        </button>
        <button type="button" className="button-primary" onClick={() => setPhase("video-only")}>
          영상만 보기
        </button>
      </ArMessage>
    );
  }

  if (phase === "video-only" && target) {
    return (
      <main className="ar-scan-fallback">
        <h1 tabIndex={-1}>{target.student_name} 의 영상</h1>
        {reason ? <p>{reason}</p> : null}
        {/* 🔑 여기서는 소리를 켠 채로 둔다 — 사람이 눌러서 재생하는 자리라 자동재생 규칙에 안 걸린다. */}
        <video src={target.video_url} controls playsInline preload="metadata" />
      </main>
    );
  }

  return (
    <div className="ar-scan-root">
      {/* 🚨 장면은 화면 전체를 덮어야 한다. 카드 안에 넣으면 카메라 영상과 캔버스가 어긋나
           AR 이 화면 구석에 뜬다(v1 7/29 QA 「우상단」). */}
      <div className="ar-scene-host" ref={hostRef} />
      <div className="ar-scan-hud">
        <p>{target?.student_name} 의 그림을 비춰 주세요</p>
        <div className="ar-scan-hud-actions">
          <button type="button" className="button-secondary" onClick={toggleSound}>
            {muted ? "소리 켜기" : "소리 끄기"}
          </button>
          <button type="button" className="button-secondary" onClick={() => setPhase("video-only")}>
            영상만 보기
          </button>
        </div>
      </div>
    </div>
  );
}

function ArMessage(
  { title, body, children }: { title: string; body: string; children?: React.ReactNode },
) {
  return (
    <main className="ar-scan-message">
      <h1 tabIndex={-1}>{title}</h1>
      <p>{body}</p>
      {children ? <div className="ar-scan-message-actions">{children}</div> : null}
    </main>
  );
}
